import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ShoppingBag, List, X, Phone, UserCircle } from "@phosphor-icons/react";
import Logo from "@/components/site/Logo";
import AccountDialog from "@/components/site/AccountDialog";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { useI18n } from "@/context/I18nContext";

const NAV = [
    { id: "menu", key: "nav.menu" },
    { id: "reviews", key: "nav.reviews" },
    { id: "gallery", key: "nav.gallery" },
    { id: "about", key: "nav.about" },
    { id: "contact", key: "nav.contact" },
];

export default function Header() {
    const { count, setOpen } = useCart();
    const { customer } = useCustomer();
    const { lang, setLang, t } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [mobile, setMobile] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled ? "bg-white/85 backdrop-blur-xl border-b border-border/60" : "bg-transparent"
            }`}
        >
            <div className="mx-auto w-full max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="logo-home" className="flex items-center gap-2.5">
                    <Logo size={42} className="rounded-full bg-white shadow-sm" />
                    <span className="font-display font-bold text-lg tracking-tight hidden sm:block">
                        J & W Deli
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV.map((n) => (
                        <a
                            key={n.id}
                            href={`#${n.id}`}
                            data-testid={`nav-${n.id}`}
                            className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors"
                        >
                            {t(n.key)}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        data-testid="lang-toggle"
                        onClick={() => setLang(lang === "en" ? "es" : "en")}
                        className="hidden sm:flex h-9 px-3 rounded-full border border-border text-xs font-bold tracking-widest uppercase hover:bg-muted transition"
                    >
                        {lang === "en" ? "ES" : "EN"}
                    </button>
                    <a
                        href="tel:+12034666226"
                        data-testid="header-call"
                        className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full border border-border hover:bg-muted transition"
                        aria-label="Call"
                    >
                        <Phone size={16} weight="bold" />
                    </a>
                    <button
                        data-testid="open-account"
                        onClick={() => setAccountOpen(true)}
                        className={`h-9 w-9 flex items-center justify-center rounded-full border transition ${
                            customer
                                ? "border-primary/40 bg-primary/10 text-primary"
                                : "border-border hover:bg-muted"
                        }`}
                        aria-label={customer ? "Mi cuenta" : "Iniciar sesión"}
                        title={customer ? customer.name : lang === "es" ? "Mi cuenta" : "My account"}
                    >
                        <UserCircle size={18} weight={customer ? "fill" : "bold"} />
                    </button>
                    <button
                        data-testid="open-cart"
                        onClick={() => setOpen(true)}
                        className="relative h-9 px-4 rounded-full bg-foreground text-background flex items-center gap-2 font-bold text-sm hover:scale-105 transition"
                    >
                        <ShoppingBag size={16} weight="bold" />
                        <span data-testid="cart-count">{count}</span>
                    </button>
                    <button
                        data-testid="mobile-menu-toggle"
                        className="md:hidden h-9 w-9 grid place-items-center rounded-full border border-border"
                        onClick={() => setMobile((m) => !m)}
                        aria-label="Menu"
                    >
                        {mobile ? <X size={18} /> : <List size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {mobile && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden overflow-hidden bg-white border-b border-border"
                    >
                        <div className="px-6 py-4 flex flex-col gap-3">
                            {NAV.map((n) => (
                                <a
                                    key={n.id}
                                    href={`#${n.id}`}
                                    onClick={() => setMobile(false)}
                                    data-testid={`mobile-nav-${n.id}`}
                                    className="py-2 text-base font-semibold"
                                >
                                    {t(n.key)}
                                </a>
                            ))}
                            <button
                                data-testid="mobile-lang-toggle"
                                onClick={() => setLang(lang === "en" ? "es" : "en")}
                                className="self-start mt-2 px-3 py-1 rounded-full border border-border text-xs font-bold uppercase"
                            >
                                {lang === "en" ? "ES" : "EN"}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
        </header>
    );
}
